import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import * as monthUtils from '@actual-app/core/shared/months';

import type { GrupoHistoria } from '#components/mobile/historia/escribirHistoria';
import {
  leerExtractoHistoria,
  paraImportar,
} from '#components/mobile/historia/leerExtractoHistoria';
import type { ExtractoLeido } from '#components/mobile/historia/leerExtractoHistoria';
import { EXTENSIONES } from '#components/mobile/importar/importar';
import { CONFIG_VACIA, conTipo } from '#components/mobile/nomina/nomina';
import { useConfigNomina } from '#components/mobile/nomina/useNomina';
import { ID_ANADIR_MOVIMIENTO } from '#components/mobile/opciones/opciones';
import { irAPestana, RUTA_INICIO } from '#components/mobile/ui/pestanas';
import { color } from '#components/mobile/ui/tokens';
import { useGlobalPref } from '#hooks/useGlobalPref';
import { useNavigate } from '#hooks/useNavigate';
import { useReducedMotion } from '#hooks/useReducedMotion';
import { SheetNameProvider } from '#hooks/useSheetName';

import { CabeceraPaso } from './CabeceraPaso';
import { PERFIL_POR_DEFECTO } from './catalogo';
import type { IdCofre, MetaElegida, Perfil } from './catalogo';
import type { CuentaTravesia } from './escribir';
import { travesiaPendiente, ULTIMO_PASO } from './estado';
import { Faros } from './Faros';
import { FinalInicio } from './pasos/FinalInicio';
import { HojaCategorias } from './pasos/HojaCategorias';
import { HojaCuentas } from './pasos/HojaCuentas';
import type { ModoSaldo, PeticionExtracto } from './pasos/HojaCuentas';
import { HojaExtracto } from './pasos/HojaExtracto';
import { HojaNomina } from './pasos/HojaNomina';
import type { EleccionNomina } from './pasos/HojaNomina';
import { HojaNotificaciones } from './pasos/HojaNotificaciones';
import { HojaObjetivo } from './pasos/HojaObjetivo';
import { HojaOrdenar } from './pasos/HojaOrdenar';
import { HojaVive } from './pasos/HojaVive';
import { PasoRumbo } from './pasos/PasoRumbo';
import { RepartoPlan } from './pasos/RepartoPlan';
import { TravesiaProvider } from './TravesiaContext';
import type { PestanaTravesia, TravesiaValor } from './TravesiaContext';
import { useEscribirTravesia } from './useEscribirTravesia';
import { useRecuperarTravesia } from './useRecuperarTravesia';

/** Las cuentas van después de las categorías (para ordenar la historia). */
const PASO_CUENTAS = 5;
/** «Rumbo también se planifica» (1-nov-2026): tras cuentas y nómina. */
const PASO_RUMBO = 6;
const PASO_OBJETIVO = 7;
const PASO_REPARTO = 8;
const PASO_NOTIFICACIONES = 9;

/** Pantalla real de cada paso (plan técnico §1). */
export function rutaDePaso(paso: number): string {
  if (paso === PASO_CUENTAS) {
    return '/accounts';
  }
  if (paso >= PASO_NOTIFICACIONES) {
    return '/inicio';
  }
  return '/budget';
}

/** Pestaña que se ve encendida en cada paso. */
export function pestanaDePaso(paso: number): PestanaTravesia {
  if (paso === PASO_CUENTAS) {
    return 'cuentas';
  }
  return paso >= PASO_NOTIFICACIONES ? 'inicio' : 'plan';
}

/** Lo que tarda la hoja de Cuentas en bajar y la cuenta en aparecer. */
const ESPERA_CUENTA = 1300;

/** Entrada de historial que frena el «Atrás» del sistema en la travesía. */
function ponerGuarda() {
  if (!window.history.state?.travesiaGuarda) {
    window.history.pushState(
      { ...window.history.state, travesiaGuarda: true },
      '',
    );
  }
}

type PrimeraTravesiaProps = { children: ReactNode };

/**
 * Anfitrión de los pasos 2–10 de la «Primera travesía». No pinta pantallas
 * propias: lleva a la ruta real de cada paso (Plan, Cuentas, Inicio) y
 * pone encima su hoja o a Gavi; las pantallas de detrás leen el contexto
 * (barra atenuada, teclado con «Asignar lo sugerido», Plan vacío…). Solo en
 * pantalla estrecha; en ancha, si está pendiente, aplica «Saltar» en
 * silencio. Tras la travesía, los dos faros.
 */
export function PrimeraTravesia({ children }: PrimeraTravesiaProps) {
  const { t } = useTranslation();
  const { isNarrowWidth } = useResponsive();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const quieto = useReducedMotion();
  const escribir = useEscribirTravesia();
  const { estado } = escribir;
  const [nombrePerfil] = useGlobalPref('nombrePerfil');
  // Barco recién botado sin la marca de la travesía: se le pone.
  useRecuperarTravesia(isNarrowWidth);

  const [ocupado, setOcupado] = useState(false);
  /** Tras «Saltar»: se ve el paso final con su mensaje hasta «Empezar a navegar». */
  const [saltado, setSaltado] = useState(false);
  const [bajando, setBajando] = useState(false);
  /** Paso 3, tras las cuentas: «¿Cobras una nómina?» (pagas extra). */
  const [preguntaNomina, setPreguntaNomina] = useState(false);
  const { guardar: guardarNomina } = useConfigNomina();
  const [brujula, setBrujula] = useState(false);
  const [autoPulsa, setAutoPulsa] = useState(false);
  const autoRef = useRef<(() => Promise<void>) | null>(null);
  /** Paso 5 con extracto: lo pedido y lo leído del archivo. */
  const [extracto, setExtracto] = useState<{
    peticion: PeticionExtracto;
    leido: ExtractoLeido;
  } | null>(null);
  const [modoSaldo, setModoSaldo] = useState<ModoSaldo>('extracto');
  /** Tras el extracto: los comercios que Gavi ordena y cuántos ordenó. */
  const [orden, setOrden] = useState<{
    grupos: GrupoHistoria[];
    elegidos: GrupoHistoria[];
    colocados: number | null;
  } | null>(null);

  const pendiente = travesiaPendiente(estado);
  // Sin Gavi (elegido en el astillero): «Saltar» en silencio, como en ancha.
  const sinGavi = estado?.silencio === true;
  const paso = saltado
    ? ULTIMO_PASO
    : pendiente && !sinGavi && estado
      ? estado.paso
      : null;
  const enCurso = isNarrowWidth && paso !== null;
  const mes = monthUtils.currentMonth();

  // Pantalla ancha o sin Gavi: no hay travesía; se aplica «Saltar» en
  // silencio (P3).
  const silencio = useRef(false);
  useEffect(() => {
    if ((isNarrowWidth && !sinGavi) || !pendiente || silencio.current) {
      return;
    }
    silencio.current = true;
    escribir.saltar().catch(() => {
      silencio.current = false;
    });
  }, [isNarrowWidth, sinGavi, pendiente, escribir]);

  // Paso 2: el Plan se ve vacío (fuera las categorías de fábrica).
  const limpiado = useRef(false);
  useEffect(() => {
    if (!enCurso || paso !== 2 || limpiado.current) {
      return;
    }
    limpiado.current = true;
    escribir.limpiarFabrica().catch(() => {
      limpiado.current = false;
    });
  }, [enCurso, paso, escribir]);

  // Cada paso en su pantalla real. En el paso final, salir de Inicio (una
  // pestaña o «Añadir movimiento» de la brújula) termina la travesía.
  const llegoInicio = useRef(false);
  useEffect(() => {
    if (!enCurso || paso === null) {
      llegoInicio.current = false;
      return;
    }
    const ruta = rutaDePaso(paso);
    if (paso !== ULTIMO_PASO) {
      // Con «Atrás» desde el final, al volver a él no debe darse por acabada.
      llegoInicio.current = false;
    }
    if (paso === ULTIMO_PASO) {
      if (pathname === ruta) {
        llegoInicio.current = true;
      } else if (llegoInicio.current) {
        llegoInicio.current = false;
        void acabar();
        return;
      }
    }
    if (pathname !== ruta) {
      // Con `state` propio: si no, `useNavigate` ve que `ruta` es la pantalla
      // de antes (al volver un paso) y hace `navigate(-1)`, que saldría de la
      // guarda del historial en vez de cambiar de pantalla.
      void navigate(ruta, { replace: true, state: { travesiaPaso: paso } });
    }
    // `acabar` y `navigate` no deciden cuándo mirar la ruta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enCurso, paso, pathname]);

  /** Un acto: botones quietos mientras se escribe; si falla, no avanza. */
  async function acto(fn: () => Promise<void>) {
    if (ocupado) {
      return;
    }
    setOcupado(true);
    try {
      await fn();
    } catch {
      // El paso no avanza y su botón vuelve a estar activo.
    } finally {
      setOcupado(false);
    }
  }

  const ids = estado?.ids;

  /**
   * Como `acto`, pero el error llega a quien llama (el paso no cambia de
   * pantalla si no se pudo escribir).
   */
  async function actoQueFalla(fn: () => Promise<void>) {
    if (ocupado) {
      throw new Error('ocupado');
    }
    setOcupado(true);
    try {
      await fn();
    } finally {
      setOcupado(false);
    }
  }

  /** El paso 6 no tocaba (ya paga, sin servidor…): «Atrás» desde el 7 lo salta. */
  const rumboNoToca = useRef(false);

  function alSeguirRumbo() {
    void acto(() => escribir.guardarEstado({ paso: PASO_OBJETIVO }));
  }

  function alVivir(perfil: Perfil) {
    void acto(() => escribir.guardarEstado({ paso: 3, ...perfil }));
  }

  /** La hoja baja mientras la cuenta aparece detrás; luego, el objetivo. */
  async function bajarYSeguir() {
    setBajando(true);
    await new Promise(r => window.setTimeout(r, quieto ? 300 : ESPERA_CUENTA));
    // Antes del objetivo, la pregunta de la nómina (mismo paso).
    setPreguntaNomina(true);
    setBajando(false);
    setExtracto(null);
    setOrden(null);
  }

  function alAnadirCuentas(cuentas: CuentaTravesia[]) {
    void acto(async () => {
      if (!ids?.cuentas?.length) {
        await escribir.crearCuentas(cuentas);
      }
      await bajarYSeguir();
    });
  }

  /** «Elegir el archivo»: se lee el extracto y se enseña su saldo. */
  function alPedirExtracto(peticion: PeticionExtracto) {
    void acto(async () => {
      const rutas = await window.Actual.openFileDialog({
        filters: [{ name: t('Bank statements'), extensions: EXTENSIONES }],
      });
      const ruta = rutas?.[0];
      if (!ruta) {
        return;
      }
      setExtracto({ peticion, leido: await leerExtractoHistoria(ruta) });
    });
  }

  function alConfirmarSaldo(saldoHoy: number) {
    const leido = extracto?.leido;
    if (!extracto || leido?.tipo !== 'ok') {
      return;
    }
    void acto(async () => {
      if (!ids?.cuentas?.length) {
        await escribir.crearCuentasConExtracto(
          {
            nombre: extracto.peticion.nombre,
            saldoHoy,
            movimientos: paraImportar(leido.movimientos),
          },
          extracto.peticion.efectivo,
        );
      }
      const grupos = await escribir.gruposHistoria();
      if (grupos.length > 0) {
        setExtracto(null);
        setOrden({ grupos, elegidos: [], colocados: null });
        return;
      }
      await bajarYSeguir();
    });
  }

  function alOrdenar(elegidos: GrupoHistoria[]) {
    void acto(async () => {
      const colocados = await escribir.ordenar(elegidos);
      setOrden(o => o && { ...o, elegidos, colocados });
    });
  }

  function alDeshacerOrden() {
    const elegidos = orden?.elegidos ?? [];
    void acto(async () => {
      await escribir.ordenar(elegidos, true);
      setOrden(o => o && { ...o, elegidos: [], colocados: null });
    });
  }

  function alElegirNomina(
    eleccion: EleccionNomina | null,
    neta: number | null,
  ) {
    void acto(async () => {
      if (eleccion && eleccion !== 'otra') {
        guardarNomina({
          ...conTipo(CONFIG_VACIA, eleccion),
          neta: Math.max(0, neta ?? 0),
        });
      }
      await escribir.guardarEstado({ paso: PASO_CUENTAS + 1 });
      setPreguntaNomina(false);
    });
  }

  function alCrearCategorias(elegidos: IdCofre[], siguiente: number) {
    void acto(async () => {
      await escribir.crearCategorias(elegidos);
      await escribir.guardarEstado({ paso: siguiente });
    });
  }

  function alGuardarMeta(meta: MetaElegida) {
    void acto(async () => {
      if (!ids?.meta) {
        await escribir.crearMeta(meta);
      }
      await escribir.guardarEstado({ paso: PASO_REPARTO });
    });
  }

  function alRepartido() {
    void acto(async () => {
      await escribir.fijarInicio();
      setAutoPulsa(false);
      await escribir.guardarEstado({ paso: PASO_NOTIFICACIONES });
    });
  }

  /** Paso anterior (no hay antes del 2: el 0 y el 1 son del gestor). */
  const pasoAnterior =
    paso !== null && paso > 2 && !saltado
      ? paso === PASO_OBJETIVO && rumboNoToca.current
        ? PASO_CUENTAS
        : paso - 1
      : null;

  function alAtras() {
    // Desde la pregunta de la nómina se vuelve a las cuentas (mismo paso).
    if (paso === PASO_CUENTAS && preguntaNomina) {
      setPreguntaNomina(false);
      return;
    }
    if (pasoAnterior === null) {
      return;
    }
    // Lo ya creado (cuentas, categorías, meta) se queda: al volver a
    // avanzar se reutiliza en vez de duplicarse.
    if (paso === PASO_CUENTAS && orden) {
      // La cuenta ya está creada: «Atrás» no deshace nada.
      return;
    }
    if (paso === PASO_CUENTAS && extracto) {
      // Desde el saldo del extracto, «Atrás» vuelve a elegir cómo.
      setExtracto(null);
      return;
    }
    void acto(() => escribir.guardarEstado({ paso: pasoAnterior }));
  }

  // «Atrás» del sistema (gesto del iPhone, botón de Android): sin esto, el
  // navegador sale de la travesía. Se deja una entrada de historial de
  // guarda; al volver sobre ella se retrocede un paso y se repone.
  const atrasRef = useRef(alAtras);
  atrasRef.current = alAtras;
  useEffect(() => {
    // Al cambiar de pantalla con `replace` la guarda se pierde: se repone.
    if (enCurso) {
      ponerGuarda();
    }
  }, [enCurso, pathname]);
  // Al acabar (o saltar), fuera las guardas y pantallas de la travesía: se
  // vuelve a la base de Inicio y el siguiente «atrás» ya sale de la app.
  const estabaEnCurso = useRef(enCurso);
  useEffect(() => {
    if (estabaEnCurso.current && !enCurso && isNarrowWidth) {
      irAPestana(RUTA_INICIO, navigate);
    }
    estabaEnCurso.current = enCurso;
    // `navigate` cambia con cada pantalla; solo cuenta el final.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enCurso, isNarrowWidth]);
  useEffect(() => {
    if (!enCurso) {
      return;
    }
    const alVolver = (e: PopStateEvent) => {
      // Seguimos sobre la guarda: se ha cerrado algo abierto encima (una
      // hoja, el teclado de un importe), no es un «atrás» de la travesía.
      if (e.state?.travesiaGuarda) {
        return;
      }
      atrasRef.current();
      ponerGuarda();
    };
    window.addEventListener('popstate', alVolver);
    return () => window.removeEventListener('popstate', alVolver);
  }, [enCurso]);

  function alElegirNotificaciones() {
    void acto(() => escribir.guardarEstado({ paso: ULTIMO_PASO }));
  }

  function alSaltar() {
    void acto(async () => {
      await escribir.saltar();
      await escribir.fijarInicio();
      setBrujula(false);
      setSaltado(true);
    });
  }

  async function acabar() {
    setBrujula(false);
    if (saltado) {
      setSaltado(false);
      return;
    }
    await acto(() => escribir.terminar());
  }

  const valor: TravesiaValor | null =
    enCurso && paso !== null
      ? {
          // En el paso final la barra ya no se atenúa (como la maqueta).
          activa: paso < ULTIMO_PASO,
          pestanaEncendida: pestanaDePaso(paso),
          brujulaForzada: paso === ULTIMO_PASO && brujula,
          textoSugerido: paso === PASO_REPARTO,
          autoPulsa: paso === PASO_REPARTO && autoPulsa,
          onAuto:
            paso === PASO_REPARTO
              ? async () => {
                  await autoRef.current?.();
                }
              : undefined,
          bloquearListo: true,
          planVacioTexto: t('Your categories will appear here.'),
        }
      : null;

  const perfil: Perfil = {
    vive: estado?.vive ?? PERFIL_POR_DEFECTO.vive,
    mascota: estado?.mascota ?? PERFIL_POR_DEFECTO.mascota,
    hijos: estado?.hijos ?? PERFIL_POR_DEFECTO.hijos,
  };

  return (
    <TravesiaProvider value={valor}>
      {children}
      {enCurso && paso !== null && (
        <>
          {paso >= PASO_REPARTO && (
            // Los avisos «asignado X · Deshacer» del teclado taparían la
            // píldora «listos para asignar» de la que habla Gavi (y, luego,
            // el título de Inicio). En el reparto ya lo cuenta Gavi.
            <style>{'[data-avisos]{display:none!important}'}</style>
          )}
          {paso === ULTIMO_PASO &&
            brujula && (
              // Como la maqueta: «Añadir movimiento» destacada en la brújula.
              <style>{`[data-testid="opcion-${ID_ANADIR_MOVIMIENTO}"]{outline:2px solid ${color.accent}!important;outline-offset:0}`}</style>
            )}
          <CabeceraPaso
            paso={paso}
            // El paso de Rumbo es obligatorio: sin «Saltar» (siempre tiene
            // una salida honesta, «Aún no tengo dinero»).
            onSaltar={
              paso < ULTIMO_PASO && paso !== PASO_RUMBO ? alSaltar : undefined
            }
            onAtras={pasoAnterior !== null ? alAtras : undefined}
            saltarDesactivado={ocupado}
          />
          {paso === 2 && (
            <HojaVive perfil={perfil} ocupado={ocupado} onContinuar={alVivir} />
          )}
          {paso === PASO_CUENTAS && preguntaNomina && (
            <HojaNomina ocupado={ocupado} onContinuar={alElegirNomina} />
          )}
          {paso === 3 && (
            <HojaCategorias
              key="mensual"
              tipo="mensual"
              perfil={perfil}
              ocupado={ocupado}
              onCrear={e => alCrearCategorias(e, 4)}
            />
          )}
          {paso === 4 && (
            <HojaCategorias
              key="ocasional"
              tipo="ocasional"
              perfil={perfil}
              ocupado={ocupado}
              onCrear={e => alCrearCategorias(e, PASO_CUENTAS)}
            />
          )}
          {paso === PASO_CUENTAS && !preguntaNomina && orden && (
            <HojaOrdenar
              grupos={orden.grupos}
              colocados={orden.colocados}
              ocupado={ocupado}
              bajando={bajando}
              onColocar={alOrdenar}
              onDeshacer={alDeshacerOrden}
              onSeguir={() => void acto(bajarYSeguir)}
            />
          )}
          {paso === PASO_CUENTAS && !preguntaNomina && !extracto && !orden && (
            <HojaCuentas
              ocupado={ocupado}
              bajando={bajando}
              modoInicial={modoSaldo}
              onAnadir={alAnadirCuentas}
              onExtracto={alPedirExtracto}
            />
          )}
          {paso === PASO_CUENTAS && !preguntaNomina && extracto && (
            <HojaExtracto
              leido={extracto.leido}
              cuenta={extracto.peticion.nombre}
              ocupado={ocupado}
              bajando={bajando}
              onCambiar={() => alPedirExtracto(extracto.peticion)}
              onSinExtracto={() => {
                setModoSaldo('escribir');
                setExtracto(null);
              }}
              onConfirmar={alConfirmarSaldo}
            />
          )}
          {paso === PASO_RUMBO && (
            <PasoRumbo
              idRumbo={ids?.rumbo?.id}
              ocupado={ocupado}
              onCrear={(nombre, datos) =>
                actoQueFalla(async () => {
                  await escribir.crearRumbo(nombre, datos);
                })
              }
              onApartar={(datos, pendiente) =>
                actoQueFalla(() => escribir.apartarRumbo(datos, pendiente))
              }
              onSeguir={alSeguirRumbo}
              onNoToca={() => {
                rumboNoToca.current = true;
                alSeguirRumbo();
              }}
            />
          )}
          {paso === PASO_OBJETIVO && (
            <HojaObjetivo
              mes={mes}
              ocupado={ocupado}
              onGuardar={alGuardarMeta}
            />
          )}
          {paso === PASO_REPARTO && (
            <SheetNameProvider name={monthUtils.sheetForMonth(mes)}>
              <RepartoPlan
                mes={mes}
                ids={ids}
                repartirAuto={escribir.repartirAuto}
                autoRef={autoRef}
                onAutoPulsa={setAutoPulsa}
                ocupado={ocupado}
                onContinuar={alRepartido}
              />
            </SheetNameProvider>
          )}
          {paso === PASO_NOTIFICACIONES && (
            <HojaNotificaciones
              ocupado={ocupado}
              onContinuar={alElegirNotificaciones}
            />
          )}
          {paso === ULTIMO_PASO && pathname === '/inicio' && (
            <FinalInicio
              nombre={nombrePerfil ?? ''}
              saltado={saltado}
              ocupado={ocupado}
              onAbrirBrujula={() => setBrujula(true)}
              onEmpezar={() => void acabar()}
            />
          )}
        </>
      )}
      {isNarrowWidth && !enCurso && estado && !pendiente && <Faros />}
    </TravesiaProvider>
  );
}
